import { useCallback, useEffect, useRef, useState } from 'react';
import { CreditRequest, CreditRequestContext, creditsClient } from '../client/creditsClient';
import { useAuth } from '../contexts/AuthContext';
import { apiErrorMessage } from '../utils/apiErrorMessage';
import {
    REQUESTED_MESSAGE, askAgainLabel, requestFailedMessage, requestedOnLabel
} from '../utils/creditRequest';
import {
    noteCreditRequestMade, readCurrentCreditRequest, refreshCreditRequest, subscribeCreditRequest
} from './creditRequestSync';

/**
 * "Request more credits". One request per org may be open at a time, and a
 * decline starts a wait, so the control is a fact the server owns: nothing
 * open (the button), one open (the notice "Requested on 3 Oct, waiting for a
 * platform admin"), a recent decline (the notice "A platform admin declined
 * this on 3 Oct. You can ask again after 4 Oct, 14:30."), or the answer to the
 * click that was just made. The server's answer is read on mount and again
 * whenever the window regains focus, so a grant or a decline shows without a
 * reload (see `creditRequestSync`, which also refreshes the cached balance
 * when an open request is answered). It is never copied into anything
 * longer-lived.
 *
 * The buyer names no amount. The caller passes what the buyer was doing
 * (RFQ and shortfall), or nothing.
 */

export type CreditRequestPhase =
    /** Still asking the server what, if anything, is open. */
    | 'loading'
    /** Nothing open, no wait: the button. */
    | 'idle'
    /** The click is in flight. */
    | 'requesting'
    /** Just sent, in this control: Robbie says what happens next. */
    | 'requested'
    /** One is open (made here earlier, or by a teammate): the notice. */
    | 'pending'
    /** The last one was declined and the wait is not over: the notice. */
    | 'declined'
    /** The click failed and nothing changed: Robbie says why. */
    | 'failed';

export interface CreditRequestState {
    phase: CreditRequestPhase;
    /** `3 Oct`, once a request is open. */
    requestedOn?: string;
    /** `3 Oct`, in the `declined` phase: the day the decision was made. */
    declinedOn?: string;
    /** `4 Oct, 14:30`, in the `declined` phase: when the buyer may ask again. */
    askAgainAt?: string;
    /** Robbie's line for `requested` and `failed`. */
    message?: string;
    request: (context?: CreditRequestContext) => Promise<void>;
}

/** The server's codes for the two reasons it refuses a request that is not a failure. */
const PENDING_CODE = 'CREDIT_REQUEST_PENDING';
const RECENTLY_DECLINED_CODE = 'CREDIT_REQUEST_RECENTLY_DECLINED';

type Outcome = { kind: 'none' } | { kind: 'requesting' } | { kind: 'requested' } | { kind: 'failed'; message: string };

const errorCodeOf = (err: unknown): string | undefined =>
    (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;

/** The decline whose wait is still running at `now`, or null. */
const declineInForce = (current: CreditRequest | null, now: number): CreditRequest | null =>
    current?.status === 'DECLINED' && current.availableAt && new Date(current.availableAt).getTime() > now ? current : null;

export const useCreditRequest = (): CreditRequestState => {
    const { backendUser } = useAuth();
    const orgId = backendUser?.orgId;

    const [current, setCurrent] = useState<CreditRequest | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [outcome, setOutcome] = useState<Outcome>({ kind: 'none' });
    // A slow answer must not overwrite a newer one.
    const requestSeq = useRef(0);
    const hasRead = useRef(false);

    const load = useCallback(async () => {
        if (!orgId) return;
        const seq = ++requestSeq.current;
        try {
            const read = await readCurrentCreditRequest(orgId);
            if (seq !== requestSeq.current) return;
            setCurrent(read);
            // "I've asked…" confirms a click; it is not a standing state. Once
            // a read shows the request answered, it gives way to what is true.
            setOutcome((previous) => (previous.kind === 'requested' && read?.status !== 'PENDING' ? { kind: 'none' } : previous));
            hasRead.current = true;
        } catch {
            if (seq !== requestSeq.current) return;
            // Not knowing is not "nothing open". With no earlier answer the
            // button stays (the server still refuses a second request with a
            // 409); with one, it stands until a read says otherwise.
            if (!hasRead.current) setCurrent(null);
        } finally {
            if (seq === requestSeq.current) setLoaded(true);
        }
    }, [orgId]);

    useEffect(() => {
        if (!orgId) {
            setCurrent(null);
            setLoaded(true);
            return undefined;
        }
        setLoaded(false);
        hasRead.current = false;
        load();
        return subscribeCreditRequest(load);
    }, [orgId, load]);

    const request = useCallback(
        async (context: CreditRequestContext = {}) => {
            if (!orgId) {
                setOutcome({ kind: 'failed', message: requestFailedMessage("I can't tell which organization you're in yet") });
                return;
            }
            setOutcome({ kind: 'requesting' });
            try {
                const made = await creditsClient.requestCredits(orgId, context);
                noteCreditRequestMade(orgId, made);
                setCurrent(made);
                setOutcome({ kind: 'requested' });
                refreshCreditRequest();
            } catch (err) {
                const code = errorCodeOf(err);
                if (code === PENDING_CODE || code === RECENTLY_DECLINED_CODE) {
                    // A teammate got there first, or a decline is inside its wait:
                    // the notice the server's own answer produces, not a failure.
                    setOutcome({ kind: 'none' });
                    refreshCreditRequest();
                    return;
                }
                setOutcome({ kind: 'failed', message: requestFailedMessage(apiErrorMessage(err, '')) });
            }
        },
        [orgId]
    );

    const open = current?.status === 'PENDING' ? current : null;
    const requestedOn = open ? requestedOnLabel(open.createdAt) : undefined;
    const declined = declineInForce(current, Date.now());

    if (outcome.kind === 'requesting') return { phase: 'requesting', requestedOn, request };
    if (outcome.kind === 'requested') return { phase: 'requested', requestedOn, message: REQUESTED_MESSAGE, request };
    if (outcome.kind === 'failed') return { phase: 'failed', requestedOn, message: outcome.message, request };
    if (!loaded) return { phase: 'loading', request };
    if (open) return { phase: 'pending', requestedOn, request };
    if (declined) {
        return {
            phase: 'declined',
            declinedOn: requestedOnLabel(declined.decidedAt ?? declined.createdAt),
            askAgainAt: askAgainLabel(declined.availableAt as string),
            request
        };
    }
    return { phase: 'idle', request };
};

export default useCreditRequest;
